import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Avatar,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Pagination,
  Button,
  Tooltip,
  Chip,
  Card,
  CardContent,
  CardActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import GroupIcon from '@mui/icons-material/Group';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { alpha } from '@mui/material/styles';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';

const BeneficiaryGroups = () => {
  const { user, loading: authLoading } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [useGroupOpen, setUseGroupOpen] = useState(false);
  const [isUsingGroup, setIsUsingGroup] = useState(false);
  const [groupFundType, setGroupFundType] = useState('credit');
  const [groupNarration, setGroupNarration] = useState('');
  const [groupBeneficiaryAmounts, setGroupBeneficiaryAmounts] = useState({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await api.dashboard.getBeneficiaryGroups();
      const sortedGroups = (response.data || []).sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      setGroups(sortedGroups);
    } catch (err) {
      setError(err.message || 'Error fetching beneficiary groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const filteredGroups = groups.filter(
    (group) =>
      (group.name || '').toLowerCase().includes(filter.toLowerCase()) ||
      (group.description || '').toLowerCase().includes(filter.toLowerCase())
  );

  // Calculate pagination
  const totalPages = Math.ceil(filteredGroups.length / itemsPerPage);
  const paginatedGroups = filteredGroups.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleViewGroup = (group) => {
    setSelectedGroup(group);
    setViewDialogOpen(true);
  };

  const handleViewDialogClose = () => {
    setViewDialogOpen(false);
    setSelectedGroup(null);
  };

  const handleDeleteGroup = async (groupId, groupName) => {
    if (!window.confirm(`Are you sure you want to delete the group "${groupName}"?`)) {
      return;
    }

    try {
      await api.dashboard.deleteBeneficiaryGroup(groupId);
      toast.success(`Group "${groupName}" deleted successfully`);
      fetchGroups();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error(error.message || 'Failed to delete group');
    }
  };

  const handleUseGroupOpen = (group) => {
    setSelectedGroup(group);
    setUseGroupOpen(true);
    setGroupFundType('credit');
    setGroupNarration(group.description || '');
    // Initialize amounts with the group's original amounts
    const initialAmounts = {};
    group.beneficiaries.forEach(beneficiary => {
      initialAmounts[beneficiary.userId] = beneficiary.amount.toString();
    });
    setGroupBeneficiaryAmounts(initialAmounts);
  };

  const handleUseGroupClose = () => {
    setUseGroupOpen(false);
    setSelectedGroup(null);
    setGroupFundType('credit');
    setGroupNarration('');
    setGroupBeneficiaryAmounts({});
  };

  const handleGroupBeneficiaryAmountChange = (userId, amount) => {
    setGroupBeneficiaryAmounts(prev => ({ ...prev, [userId]: amount }));
  };

  const handleUseGroup = async () => {
    if (!selectedGroup) return;

    setIsUsingGroup(true);
    try {
      await api.dashboard.useBeneficiaryGroup(selectedGroup._id, {
        groupId: selectedGroup._id,
        fundType: groupFundType,
        narration: groupNarration
      });

      toast.success(`Successfully processed ${selectedGroup.beneficiaries.length} beneficiaries from group "${selectedGroup.name}"`);
      handleUseGroupClose();
      
      // Refresh groups list
      fetchGroups();
    } catch (error) {
      console.error('Error using group:', error);
      toast.error(error.message || 'Failed to use group');
    } finally {
      setIsUsingGroup(false);
    }
  };

  if (authLoading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;
  }

  if (!user || user.type !== 'superadmin') {
    return <Alert severity="error">Access denied. Only superadmins can view this page.</Alert>;
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} color="#A80A0A" gutterBottom>
        Beneficiary Groups
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage and use beneficiary groups for bulk wallet funding operations.
      </Typography>
      
      <Box sx={{ mb: 3, maxWidth: 400 }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search groups by name or description..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: 'background.paper',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              },
            },
          }}
        />
      </Box>

      {loading ? (
        <LoadingSpinner size="medium" />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <Paper 
            sx={{ 
              mt: 2, 
              width: '100%', 
              overflow: 'auto',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
              '& .MuiTableHead-root': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },
              '& .MuiTableCell-head': {
                fontWeight: 700,
                color: theme.palette.text.primary,
                borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              },
              '& .MuiTableCell-body': {
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              },
              '& .MuiTableRow-root:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.02),
              },
            }}
          >
            <TableContainer sx={{ minWidth: isMobile ? 600 : 0 }}>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Group Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Beneficiaries</TableCell>
                    <TableCell>Total Amount</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Last Used</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedGroups.map((group) => (
                    <TableRow 
                      key={group._id}
                      sx={{
                        '&:last-child td': { borderBottom: 0 },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <GroupIcon color="primary" />
                          <Typography variant="body2" fontWeight={600}>
                            {group.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                          {group.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${group.beneficiaries.length} users`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          ₦{group.totalAmount.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {group.createdByName || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(group.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {group.lastUsed ? new Date(group.lastUsed).toLocaleDateString() : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewGroup(group)}
                              sx={{
                                color: theme.palette.primary.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                },
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Use Group">
                            <IconButton
                              size="small"
                              onClick={() => handleUseGroupOpen(group)}
                              sx={{
                                color: theme.palette.success.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                                },
                              }}
                            >
                              <PlayArrowIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Group">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteGroup(group._id, group.name)}
                              sx={{
                                color: theme.palette.error.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          
          <Box display="flex" justifyContent="center" mt={3} mb={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 1,
                  '&.Mui-selected': {
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  },
                },
              }}
            />
          </Box>
        </>
      )}

      {/* View Group Details Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={handleViewDialogClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 32px 64px rgba(0,0,0,0.4)' 
              : '0 32px 64px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <GroupIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              {selectedGroup?.name}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleViewDialogClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedGroup && (
            <>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {selectedGroup.description}
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="primary" fontWeight={700}>
                      {selectedGroup.beneficiaries.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Beneficiaries
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="success.main" fontWeight={700}>
                      ₦{selectedGroup.totalAmount.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="info.main" fontWeight={700}>
                      {new Date(selectedGroup.createdAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Created
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" color="warning.main" fontWeight={700}>
                      {selectedGroup.lastUsed ? new Date(selectedGroup.lastUsed).toLocaleDateString() : 'Never'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last Used
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Beneficiaries
              </Typography>
              
              <List>
                {selectedGroup.beneficiaries.map((beneficiary, index) => (
                  <React.Fragment key={beneficiary.userId}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                          {(beneficiary.userName || '?')[0]}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={beneficiary.userName}
                        secondary={beneficiary.userEmail}
                      />
                      <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          ₦{beneficiary.amount.toLocaleString()}
                        </Typography>
                        {beneficiary.narration && (
                          <Typography variant="caption" color="text.secondary">
                            {beneficiary.narration}
                          </Typography>
                        )}
                      </Box>
                    </ListItem>
                    {index < selectedGroup.beneficiaries.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleViewDialogClose}>
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlayArrowIcon />}
            onClick={() => {
              handleViewDialogClose();
              handleUseGroupOpen(selectedGroup);
            }}
          >
            Use Group
          </Button>
        </DialogActions>
      </Dialog>

      {/* Use Group Dialog */}
      <Dialog
        open={useGroupOpen}
        onClose={handleUseGroupClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Use Group for Bulk Funding
          <IconButton
            aria-label="close"
            onClick={handleUseGroupClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedGroup && (
            <>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {selectedGroup.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {selectedGroup.description}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Transaction Type</InputLabel>
                    <Select
                      value={groupFundType}
                      label="Transaction Type"
                      onChange={(e) => setGroupFundType(e.target.value)}
                    >
                      <MenuItem value="credit">Credit</MenuItem>
                      <MenuItem value="debit">Debit</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Narration"
                    value={groupNarration}
                    onChange={(e) => setGroupNarration(e.target.value)}
                    placeholder="Enter transaction narration"
                  />
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Beneficiaries ({selectedGroup.beneficiaries.length})
              </Typography>
              
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                {selectedGroup.beneficiaries.map((beneficiary, index) => (
                  <Paper
                    key={beneficiary.userId}
                    sx={{
                      p: 2,
                      mb: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2" fontWeight={600}>
                          {beneficiary.userName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {beneficiary.userEmail}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Amount"
                          type="number"
                          value={groupBeneficiaryAmounts[beneficiary.userId] || ''}
                          onChange={e => handleGroupBeneficiaryAmountChange(beneficiary.userId, e.target.value)}
                          inputProps={{ min: 0, step: 1 }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                          }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="caption" color="text.secondary">
                          {beneficiary.narration || 'No narration'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Box>

              <Paper sx={{ p: 2, mt: 2, backgroundColor: alpha(theme.palette.info.main, 0.1) }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Summary
                </Typography>
                <Typography variant="body2">
                  Total Beneficiaries: {selectedGroup.beneficiaries.length}
                </Typography>
                <Typography variant="body2">
                  Total Amount: ₦{
                    selectedGroup.beneficiaries.reduce((sum, b) =>
                      sum + Number(groupBeneficiaryAmounts[b.userId] || 0), 0
                    ).toLocaleString()
                  }
                </Typography>
                <Typography variant="body2">
                  Transaction Type: {groupFundType === 'credit' ? 'Credit' : 'Debit'}
                </Typography>
                {groupNarration && (
                  <Typography variant="body2">
                    Narration: {groupNarration}
                  </Typography>
                )}
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleUseGroupClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleUseGroup}
            variant="contained"
            color="primary"
            disabled={isUsingGroup || !selectedGroup}
            startIcon={isUsingGroup ? <CircularProgress size={16} /> : null}
          >
            {isUsingGroup ? 'Processing...' : `Fund ${selectedGroup?.beneficiaries?.length || 0} Users`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BeneficiaryGroups; 